/**
 * Outreach Service
 *
 * Static methods for managing LinkedIn outreach contacts and activities.
 * Follows LeadManagementService.ts pattern.
 *
 * Collections:
 *   linkedin_outreach/{docId}
 *   linkedin_outreach/{docId}/activities/{activityId}
 */

import { db } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  onSnapshot,
  orderBy,
  limit,
  Timestamp,
  Unsubscribe,
  arrayUnion,
  increment,
} from 'firebase/firestore';
import type {
  OutreachContact,
  OutreachActivity,
  OutreachStatus,
  OutreachDashboardStats,
  ContactMethod,
  ActivityDirection,
  ActivityOutcome,
  Priority,
  ICPFit,
} from '../types/outreach';
import { toDate } from '../types/leads';
import { normalizeLinkedInUrl, ALL_OUTREACH_STATUSES } from '../types/outreach';

const OUTREACH_COLLECTION = 'linkedin_outreach';

/** Strip undefined values — Firestore rejects them in addDoc/setDoc. */
function stripUndefined<T extends Record<string, any>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, v]) => v !== undefined)
  ) as T;
}

function getOutreachCollection() {
  return collection(db, OUTREACH_COLLECTION);
}

function getActivitiesCollection(contactId: string) {
  return collection(db, OUTREACH_COLLECTION, contactId, 'activities');
}

export class OutreachService {
  /**
   * Fetch all outreach contacts with optional filters.
   */
  static async getContacts(filters?: {
    status?: OutreachStatus;
    priority?: Priority;
    icpFit?: ICPFit;
    limit?: number;
  }): Promise<OutreachContact[]> {
    try {
      const constraints: any[] = [orderBy('createdAt', 'desc')];

      if (filters?.status) {
        constraints.unshift(where('status', '==', filters.status));
      }
      if (filters?.priority) {
        constraints.unshift(where('priority', '==', filters.priority));
      }
      if (filters?.limit) {
        constraints.push(limit(filters.limit));
      }

      const q = query(getOutreachCollection(), ...constraints);
      const snapshot = await getDocs(q);

      return snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
      } as OutreachContact));
    } catch (error) {
      console.error('Error fetching outreach contacts:', error);
      return [];
    }
  }

  /**
   * Subscribe to real-time contact updates.
   */
  static subscribeToContacts(
    callback: (contacts: OutreachContact[]) => void
  ): Unsubscribe {
    const q = query(getOutreachCollection(), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
      const contacts = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
      } as OutreachContact));
      callback(contacts);
    }, (error) => {
      console.error('Error subscribing to outreach contacts:', error);
    });
  }

  /**
   * Get a single contact by ID.
   */
  static async getContact(docId: string): Promise<OutreachContact | null> {
    try {
      const docRef = doc(db, OUTREACH_COLLECTION, docId);
      const snap = await getDoc(docRef);
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() } as OutreachContact;
    } catch (error) {
      console.error('Error fetching contact:', docId, error);
      return null;
    }
  }

  /**
   * Create a new outreach contact.
   * Checks for LinkedIn URL dedup before creating.
   */
  static async createContact(data: {
    firstName: string;
    lastName: string;
    company: string;
    linkedinUrl?: string;
    email?: string;
    phone?: string;
    role?: string;
    location?: string;
    website?: string;
    birthday?: string;
    estimatedBrokerCount?: string;
    numberOfOffices?: string;
    aggregator?: string;
    icpFit?: ICPFit;
    priority?: Priority;
    foundVia?: string;
    notes?: string;
    tags?: string[];
  }): Promise<string> {
    // Dedup check on LinkedIn URL
    if (data.linkedinUrl) {
      const normalized = normalizeLinkedInUrl(data.linkedinUrl);
      const q = query(
        getOutreachCollection(),
        where('linkedinUrl', '==', normalized)
      );
      const existing = await getDocs(q);
      if (!existing.empty) {
        throw new Error(`Contact with this LinkedIn URL already exists: ${existing.docs[0].data().firstName} ${existing.docs[0].data().lastName}`);
      }
      data.linkedinUrl = normalized;
    }

    const contactData = stripUndefined({
      ...data,
      status: 'identified' as OutreachStatus,
      statusHistory: [{
        from: null,
        to: 'identified',
        changedBy: 'system',
        changedAt: new Date(),
        note: 'Contact created',
      }],
      activityCount: 0,
      tags: data.tags || [],
      createdAt: Timestamp.now(),
    });

    const docRef = await addDoc(getOutreachCollection(), contactData);
    return docRef.id;
  }

  /**
   * Update contact fields.
   */
  static async updateContact(
    docId: string,
    updates: Partial<Omit<OutreachContact, 'id' | 'createdAt' | 'statusHistory'>>
  ): Promise<void> {
    const docRef = doc(db, OUTREACH_COLLECTION, docId);
    if (updates.linkedinUrl) {
      updates.linkedinUrl = normalizeLinkedInUrl(updates.linkedinUrl);
    }
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Update contact status with audit trail.
   */
  static async updateContactStatus(
    docId: string,
    newStatus: OutreachStatus,
    changedBy: string,
    note?: string
  ): Promise<void> {
    const docRef = doc(db, OUTREACH_COLLECTION, docId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) throw new Error('Contact not found');

    const currentStatus = snap.data().status || 'identified';
    const statusChange = {
      from: currentStatus,
      to: newStatus,
      changedBy,
      changedAt: new Date(),
      ...(note ? { note } : {}),
    };

    await updateDoc(docRef, {
      status: newStatus,
      statusHistory: arrayUnion(statusChange),
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Set next follow-up date.
   */
  static async setFollowUpDate(
    docId: string,
    date: Date | null
  ): Promise<void> {
    const docRef = doc(db, OUTREACH_COLLECTION, docId);
    await updateDoc(docRef, {
      nextFollowUpDate: date ? Timestamp.fromDate(date) : null,
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Add a note to a contact.
   */
  static async addNote(
    docId: string,
    note: string,
    author: string
  ): Promise<void> {
    const docRef = doc(db, OUTREACH_COLLECTION, docId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) throw new Error('Contact not found');

    const currentNotes = snap.data().notes || '';
    const timestamp = new Date().toLocaleString('en-AU', { timeZone: 'Australia/Brisbane' });
    const newNote = `[${timestamp} - ${author}] ${note}`;
    const updatedNotes = currentNotes ? `${currentNotes}\n${newNote}` : newNote;

    await updateDoc(docRef, {
      notes: updatedNotes,
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Update tags on a contact.
   */
  static async updateTags(docId: string, tags: string[]): Promise<void> {
    const docRef = doc(db, OUTREACH_COLLECTION, docId);
    await updateDoc(docRef, {
      tags,
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Link an outreach contact to an inbound marketing lead.
   */
  static async linkToLead(
    docId: string,
    leadRef: string,
    leadType: string,
    leadId: string
  ): Promise<void> {
    const docRef = doc(db, OUTREACH_COLLECTION, docId);
    await updateDoc(docRef, {
      linkedLeadRef: leadRef,
      linkedLeadType: leadType,
      linkedLeadId: leadId,
      updatedAt: Timestamp.now(),
    });
  }

  // ---- Activity Methods ----

  /**
   * Get activities for a contact.
   */
  static async getActivities(contactId: string): Promise<OutreachActivity[]> {
    try {
      const q = query(
        getActivitiesCollection(contactId),
        orderBy('timestamp', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
      } as OutreachActivity));
    } catch (error) {
      console.error('Error fetching activities for:', contactId, error);
      return [];
    }
  }

  /**
   * Subscribe to real-time activity updates for a contact.
   */
  static subscribeToActivities(
    contactId: string,
    callback: (activities: OutreachActivity[]) => void
  ): Unsubscribe {
    const q = query(
      getActivitiesCollection(contactId),
      orderBy('timestamp', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      const activities = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
      } as OutreachActivity));
      callback(activities);
    }, (error) => {
      console.error(`Error subscribing to activities for ${contactId}:`, error);
    });
  }

  /**
   * Add an activity to a contact.
   */
  static async addActivity(
    contactId: string,
    data: {
      contactMethod: ContactMethod;
      direction: ActivityDirection;
      summary: string;
      content?: string;
      response?: string;
      outcome?: ActivityOutcome;
      linkedinPostUrl?: string;
    },
    createdBy: string
  ): Promise<string> {
    const activityData = stripUndefined({
      ...data,
      timestamp: Timestamp.now(),
      createdBy,
    });

    const activityRef = await addDoc(
      getActivitiesCollection(contactId),
      activityData
    );

    // Update parent contact
    const contactRef = doc(db, OUTREACH_COLLECTION, contactId);
    await updateDoc(contactRef, {
      activityCount: increment(1),
      lastContactedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    return activityRef.id;
  }

  // ---- Dashboard Stats ----

  /**
   * Get dashboard statistics.
   */
  static async getDashboardStats(): Promise<OutreachDashboardStats> {
    const stats: OutreachDashboardStats = {
      totalContacts: 0,
      contactsThisWeek: 0,
      responseRate: 0,
      conversionRate: 0,
      byStatus: {} as Record<OutreachStatus, number>,
      recentActivities: [],
      followUpsDueToday: [],
    };

    // Initialize status counts
    ALL_OUTREACH_STATUSES.forEach(s => { stats.byStatus[s] = 0; });

    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date(now);
    todayEnd.setHours(23, 59, 59, 999);

    try {
      const q = query(getOutreachCollection(), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      let messagedCount = 0;
      let respondedCount = 0;

      snapshot.docs.forEach(d => {
        const contact = { id: d.id, ...d.data() } as OutreachContact;
        stats.totalContacts++;

        // Status counts
        if (stats.byStatus[contact.status] !== undefined) {
          stats.byStatus[contact.status]++;
        }

        // Weekly count
        const createdAt = toDate(contact.createdAt);
        if (createdAt >= startOfWeek) stats.contactsThisWeek++;

        // Response tracking
        if (['messaged', 'responded', 'warm', 'discovery_booked', 'demo_booked', 'converted', 'not_interested', 'no_response'].includes(contact.status)) {
          messagedCount++;
        }
        if (['responded', 'warm', 'discovery_booked', 'demo_booked', 'converted'].includes(contact.status)) {
          respondedCount++;
        }

        // Follow-ups due today
        if (contact.nextFollowUpDate) {
          const followUp = toDate(contact.nextFollowUpDate);
          if (followUp >= todayStart && followUp <= todayEnd) {
            stats.followUpsDueToday.push(contact);
          }
        }
      });

      // Calculate rates
      if (messagedCount > 0) {
        stats.responseRate = Math.round((respondedCount / messagedCount) * 100);
      }
      if (stats.totalContacts > 0) {
        stats.conversionRate = Math.round((stats.byStatus.converted / stats.totalContacts) * 100);
      }

      // Sort follow-ups by priority
      const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
      stats.followUpsDueToday.sort((a, b) =>
        (priorityOrder[a.priority || 'medium'] || 1) - (priorityOrder[b.priority || 'medium'] || 1)
      );

    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }

    return stats;
  }

  /**
   * Get contacts with follow-ups due today or overdue.
   */
  static async getFollowUpsDue(): Promise<OutreachContact[]> {
    try {
      const now = new Date();
      now.setHours(23, 59, 59, 999);

      const q = query(
        getOutreachCollection(),
        where('nextFollowUpDate', '<=', Timestamp.fromDate(now)),
        orderBy('nextFollowUpDate', 'asc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs
        .map(d => ({ id: d.id, ...d.data() } as OutreachContact))
        .filter(c => !['converted', 'not_interested'].includes(c.status));
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
      return [];
    }
  }

  /**
   * Get stale contacts (contacted 7+ days ago, no response, no follow-up date).
   */
  static async getStaleContacts(): Promise<OutreachContact[]> {
    try {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const q = query(
        getOutreachCollection(),
        where('status', 'in', ['connection_sent', 'messaged']),
        orderBy('lastContactedAt', 'asc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs
        .map(d => ({ id: d.id, ...d.data() } as OutreachContact))
        .filter(c => {
          const lastContacted = toDate(c.lastContactedAt);
          return lastContacted < sevenDaysAgo && !c.nextFollowUpDate;
        });
    } catch (error) {
      console.error('Error fetching stale contacts:', error);
      return [];
    }
  }

  /**
   * Export contacts to CSV.
   */
  static async exportToCSV(): Promise<string> {
    const contacts = await this.getContacts();
    if (contacts.length === 0) return '';

    const headers = [
      'firstName', 'lastName', 'company', 'role', 'location', 'email', 'phone',
      'website', 'birthday', 'linkedinUrl', 'status', 'priority', 'icpFit', 'estimatedBrokerCount',
      'numberOfOffices', 'aggregator', 'foundVia', 'activityCount',
      'lastContactedAt', 'nextFollowUpDate', 'createdAt', 'tags', 'notes',
    ];

    const rows = contacts.map(contact => {
      return headers.map(h => {
        if (['createdAt', 'lastContactedAt', 'nextFollowUpDate'].includes(h)) {
          const val = (contact as any)[h];
          return val ? toDate(val).toISOString() : '';
        }
        if (h === 'tags') return (contact.tags || []).join('; ');
        const val = (contact as any)[h];
        if (val === undefined || val === null) return '';
        const str = String(val);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      }).join(',');
    });

    return [headers.join(','), ...rows].join('\n');
  }
}
